import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Loader2, Settings, CheckCircle2, XCircle } from "lucide-react";

/**
 * SA-only dialog to configure MGT-allocation eligibility per India centre.
 * Fields per row: eligibility toggle + effective_from + effective_until +
 * reason (required on save).  Applies date-effectively; historical FINAL
 * payroll snapshots are NEVER rewritten silently.
 */
export function MgtCentreConfigDialog({ token, month, onSaved }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busyCode, setBusyCode] = useState("");
  const [rows, setRows] = useState([]);
  const [eligibleForMonth, setEligibleForMonth] = useState([]);
  const [edits, setEdits] = useState({}); // {code: {eligible, from, until, reason}}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/mgt-allocation/centres-config", { token, month });
      setRows(data.centres || []);
      setEligibleForMonth(data.eligible_for_month || []);
      setEdits({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load centre config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, month]);

  const patchEdit = (code, patch) =>
    setEdits((s) => ({ ...s, [code]: { ...(s[code] || {}), ...patch } }));

  const save = async (code) => {
    const e = edits[code] || {};
    const row = rows.find((r) => r.code === code);
    if (!row) return;
    const reason = (e.reason || "").trim();
    if (reason.length < 3) {
      toast.error("Reason (min 3 chars) is required to save.");
      return;
    }
    const payload = {
      token,
      code,
      reason,
    };
    if (e.eligible !== undefined && e.eligible !== row.mgt_allocation_eligible)
      payload.mgt_allocation_eligible = !!e.eligible;
    if (e.from !== undefined && e.from !== row.mgt_allocation_effective_from)
      payload.mgt_allocation_effective_from = e.from;
    if (e.until !== undefined && e.until !== row.mgt_allocation_effective_until)
      payload.mgt_allocation_effective_until = e.until;
    if (Object.keys(payload).length === 3) {
      toast.info("No changes to save for " + code);
      return;
    }
    setBusyCode(code);
    try {
      await api.post("/mgt-allocation/centre-config", payload);
      toast.success(`${code} MGT allocation config updated`);
      await load();
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save config");
    } finally {
      setBusyCode("");
    }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}
        data-testid="mgt-centre-config-btn">
        <Settings className="w-3.5 h-3.5 mr-1.5" />
        Configure Eligible Centres
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>MGT Allocation — Eligible India Centres ({month})</DialogTitle>
          </DialogHeader>

          <div className="text-xs text-muted-foreground mb-2">
            Toggle a centre off, or set effective months (YYYY-MM). Semantics:
            a centre participates in MGT allocation for month M when
            <code className="mx-1">effective_from ≤ M ≤ effective_until</code>
            (empty means unbounded). Changes flag every saved allocation ≥ Jul 2026 as STALE — regenerate to apply.
            <b> Finalised / locked historical payroll is never rewritten.</b>
          </div>

          <div className="rounded border border-slate-200 p-2 mb-2 text-sm">
            <b>Eligible for {month}:</b>{" "}
            {eligibleForMonth.length === 0 ? "—" : eligibleForMonth.join(", ")}
            {" · "}<b>Count: {eligibleForMonth.length}</b>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-purple-600" />
              <p className="text-sm text-muted-foreground mt-2">Loading centre config…</p>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Centre</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead>Eligible for {month}</TableHead>
                    <TableHead>MGT Allocation</TableHead>
                    <TableHead>Effective From</TableHead>
                    <TableHead>Effective Until</TableHead>
                    <TableHead>Reason *</TableHead>
                    <TableHead className="text-right">Save</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const eligibleNow = eligibleForMonth.includes(r.code);
                    const e = edits[r.code] || {};
                    const val = (k, fallback) => (e[k] !== undefined ? e[k] : fallback);
                    return (
                      <TableRow key={r.code} data-testid={`mgt-centre-row-${r.code}`}>
                        <TableCell className="font-medium">
                          {r.code}
                          <div className="text-[11px] text-muted-foreground">{r.name}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={r.active ? "default" : "secondary"}>
                            {r.active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {eligibleNow ? (
                            <span className="inline-flex items-center gap-1 text-green-700 text-xs">
                              <CheckCircle2 className="w-3.5 h-3.5" /> YES
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-slate-500 text-xs">
                              <XCircle className="w-3.5 h-3.5" /> NO
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={val("eligible", r.mgt_allocation_eligible)}
                              onCheckedChange={(v) => patchEdit(r.code, { eligible: v })}
                              data-testid={`mgt-centre-toggle-${r.code}`}
                            />
                            <span className="text-xs">
                              {val("eligible", r.mgt_allocation_eligible) ? "YES" : "NO"}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input
                            className="w-28 h-8 text-xs"
                            placeholder="YYYY-MM"
                            value={val("from", r.mgt_allocation_effective_from || "")}
                            onChange={(ev) => patchEdit(r.code, { from: ev.target.value })}
                            data-testid={`mgt-centre-from-${r.code}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            className="w-28 h-8 text-xs"
                            placeholder="YYYY-MM"
                            value={val("until", r.mgt_allocation_effective_until || "")}
                            onChange={(ev) => patchEdit(r.code, { until: ev.target.value })}
                            data-testid={`mgt-centre-until-${r.code}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            className="w-44 h-8 text-xs"
                            placeholder="e.g. Closed w.e.f. Aug 2026"
                            value={val("reason", "")}
                            onChange={(ev) => patchEdit(r.code, { reason: ev.target.value })}
                            data-testid={`mgt-centre-reason-${r.code}`}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            disabled={busyCode === r.code}
                            onClick={() => save(r.code)}
                            data-testid={`mgt-centre-save-${r.code}`}
                          >
                            {busyCode === r.code && (
                              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                            )}
                            Save
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
